import { TEMPLATE_IMAGE_NAMES } from "../../../../../../../api/procedure/useProcedureCreateUpdateMutation";
import { useClientQuery } from "../../../../../../../api/queries/useClientQuery";
import { GENDERS, GENDER_DEFAULT } from "../../../../../../../consts/api";
import { useImageUploader } from "../../../../../../../hooks/useImageUploader";
import { fileUtil } from "../../../../../../../utilities/file";
import {
  IMAGE_COMPRESS_WIDTH,
  IMAGE_UPLOAD_TYPE,
} from "../../CosmeticCreateEdit.consts";
import { getCosmeticPathParams } from "../../CosmeticCreateEdit.utils";
import { PROCEDURE_AREA } from "../../shared/Info/Info.consts";
import {
  FORM_IMAGE_FIELDS_TO_TEMPLATE_NAMES,
  IS_NONE_IMAGES_REQUIRED,
  IS_ONE_IMAGE_REQUIRED,
  REQUIRED_FORM_IMAGE_FIELDS_TO_NAMES_MAP,
  TEMPLATE_IMAGES_URL,
  TEMPLATE_IMAGE_UPLOAD_NAMES,
} from "./config";

export function useUploadTemplateImages() {
  const { clientId } = getCosmeticPathParams();
  const { data: client } = useClientQuery({ clientId });

  const uploader = useImageUploader({
    uploadType: IMAGE_UPLOAD_TYPE,
    compress: {
      width: IMAGE_COMPRESS_WIDTH,
    },
  });

  const gender = (() => {
    if (client?.gender === GENDERS.notSelected) {
      return GENDER_DEFAULT;
    }

    if (client?.gender === GENDERS.nonBinary) {
      return client?.nonBinaryBodyTemplate || GENDER_DEFAULT;
    }

    return client?.gender || GENDER_DEFAULT;
  })();

  const buildImageUrl = (procedureArea, imageName) => {
    if (
      client?.gender === GENDERS.nonBinary &&
      procedureArea === PROCEDURE_AREA.face
    ) {
      return `${TEMPLATE_IMAGES_URL}/${procedureArea}/${GENDERS.nonBinary}/${imageName}`;
    }

    return `${TEMPLATE_IMAGES_URL}/${procedureArea}/${gender}/${imageName}`;
  };

  const uploadOne = async (procedureArea, templateImageName) => {
    const dataUrl = await fileUtil.readFromResource(
      buildImageUrl(procedureArea, templateImageName),
      { method: "dataUrl" },
    );

    const file = fileUtil.dataUrlToFile(dataUrl, templateImageName);
    const uploadResponse = await uploader.initiate(file);

    return uploadResponse?.uploadedFileFullPath;
  };

  const initiate = async (formValues) => {
    const {
      procedureArea,
      frontImage,
      image45Left,
      image45Right,
      templateImageNames,
    } = formValues;

    if (gender) {
      if (IS_NONE_IMAGES_REQUIRED(procedureArea)) {
        return Promise.resolve({
          isCompleted: true,
          nextTemplateImageNames: templateImageNames,
        });
      }

      if (IS_ONE_IMAGE_REQUIRED(procedureArea)) {
        if (frontImage) {
          return Promise.resolve({
            front: frontImage,
            isCompleted: true,
            nextTemplateImageNames: templateImageNames,
          });
        }

        const url = await uploadOne(
          procedureArea,
          TEMPLATE_IMAGE_UPLOAD_NAMES.front,
        );

        return {
          front: url,
          isCompleted: !!url,
          nextTemplateImageNames: url
            ? [...templateImageNames, TEMPLATE_IMAGE_NAMES.front]
            : templateImageNames,
        };
      }

      if ([frontImage, image45Left, image45Right].every(Boolean)) {
        return Promise.resolve({
          front: frontImage,
          left: image45Left,
          right: image45Right,
          isCompleted: true,
          nextTemplateImageNames: templateImageNames,
        });
      }

      const urls = await Promise.all(
        Object.entries(REQUIRED_FORM_IMAGE_FIELDS_TO_NAMES_MAP).map(
          ([field, name]) =>
            formValues[field]
              ? new Promise((res) =>
                  res({
                    url: formValues[field],
                    templateName: "",
                  }),
                )
              : (async () => ({
                  url: await uploadOne(procedureArea, name),
                  templateName: FORM_IMAGE_FIELDS_TO_TEMPLATE_NAMES[field],
                }))(),
        ),
      );

      return {
        front: urls[0].url,
        left: urls[1].url,
        right: urls[2].url,
        isCompleted: urls.map((u) => u.url).every(Boolean),
        nextTemplateImageNames: [
          ...templateImageNames,
          ...urls.filter((u) => u.templateName).map((u) => u.templateName),
        ],
      };
    }

    throw new Error("Cannot upload template images");
  };

  return {
    initiate,
    buildImageUrl,
  };
}
